import { useMemo } from 'react';

import { hasAbTestBlockingDelete } from 'src/components/dashboard-pages/solve-insights/common/abTestUtils';
import { useIsAbTestAvailable } from 'src/hooks/useIsAbTestAvailable';
import { variantLookupKey } from 'src/pages/workflow-builder-edit/utils/buildVariantLookup';
import { ABTestVariantInfo } from 'src/services/insights/types';
import { useGetAutoflowVersionHistoryV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';

type UseActiveAbTestForWorkflowResult =
  | {
      activeAbTest: ABTestVariantInfo;
      /** True if any version has a draft or running AB test (blocks delete) */
      hasAbTest: boolean;
      isWorkflowInActiveAbTest: true;
      /** Version keys (version-versionType) that are part of the active AB test */
      testedVersions: Set<string>;
    }
  | {
      activeAbTest: undefined;
      /** True if any version has a draft or running AB test (blocks delete) */
      hasAbTest: boolean;
      isWorkflowInActiveAbTest: false;
      testedVersions: Set<string>;
    };

const EMPTY_SET = new Set<string>();

/**
 * Hook that checks if the current workflow (any version) is part of an active AB test.
 * Returns the AB test variant info and which version numbers are under test.
 * Gated behind the solve_ab_test feature flag — returns inactive defaults when disabled.
 */
export const useActiveAbTestForWorkflow = (
  intentWorkflowId: string,
): UseActiveAbTestForWorkflowResult => {
  const isAbTestAvailable = useIsAbTestAvailable();

  const { data: versionHistory = [] } = useGetAutoflowVersionHistoryV2Query(
    intentWorkflowId,
    { skip: !intentWorkflowId || !isAbTestAvailable },
  );

  return useMemo(() => {
    let firstActiveVariant: ABTestVariantInfo | undefined;
    const versions = new Set<string>();
    let foundAbTestBlockingDelete = false;

    for (const version of versionHistory) {
      if (!version.ab_test_variants) {
        continue;
      }
      if (!foundAbTestBlockingDelete) {
        foundAbTestBlockingDelete = hasAbTestBlockingDelete(
          version.ab_test_variants,
        );
      }
      for (const variant of version.ab_test_variants) {
        if (variant.ab_test_status === 'running') {
          if (!firstActiveVariant) {
            firstActiveVariant = variant;
          }
          versions.add(
            variantLookupKey(
              variant.intent_workflow_version,
              variant.version_type,
            ),
          );
        }
      }
    }

    if (firstActiveVariant) {
      return {
        activeAbTest: firstActiveVariant,
        hasAbTest: foundAbTestBlockingDelete,
        isWorkflowInActiveAbTest: true as const,
        testedVersions: versions,
      };
    }
    return {
      activeAbTest: undefined,
      hasAbTest: foundAbTestBlockingDelete,
      isWorkflowInActiveAbTest: false as const,
      testedVersions: versions.size > 0 ? versions : EMPTY_SET,
    };
  }, [versionHistory]);
};
